import type { NumericRange } from '../types';

export type Rgb = readonly [r: number, g: number, b: number];

/**
 * The sequential ramp: Ardoq's blue family, light to dark, one hue.
 *
 * One hue rather than a rainbow. A rainbow ramp is not ordered - viewers cannot say
 * whether green outranks orange without consulting the legend for every reading - and its
 * bands land at accidental places, so it manufactures edges in smooth data and hides real
 * ones where two hues happen to sit close in lightness. Light-to-dark on a single hue is
 * ordered by construction and survives greyscale printing and colour-vision deficiency.
 */
const SEQUENTIAL_BLUE: Rgb[] = [
  [239, 246, 255], // blue50
  [219, 234, 254], // blue100
  [191, 219, 254], // blue200
  [147, 197, 253], // blue300
  [96, 165, 250], // blue400
  [59, 130, 246], // blue500
  [37, 99, 235], // blue600
  [29, 78, 216], // blue700
  [30, 64, 175], // blue800
  [30, 58, 138], // blue900
  [23, 37, 84], // blue950
];

/**
 * Available for comparison against a rainbow reference, and deliberately not the default.
 * Kept so the two can be put side by side in a story rather than argued about.
 */
const SPECTRAL: Rgb[] = [
  [23, 37, 84],
  [37, 99, 235],
  [32, 223, 143],
  [254, 154, 0],
  [248, 59, 75],
];

/**
 * One neutral tone end to end, for a surface drawn like a technical illustration: value is read
 * from the geometry, the draped grid and the axes, which frees brightness for shading.
 */
const PAPER: Rgb[] = [
  [214, 219, 228],
  [214, 219, 228],
];

export const SEQUENTIAL_RAMPS = {
  blue: SEQUENTIAL_BLUE,
  spectral: SPECTRAL,
  paper: PAPER,
} as const;

export type SequentialRampName = keyof typeof SEQUENTIAL_RAMPS;

/**
 * Categorical fallback for component types that carry no colour of their own.
 *
 * Four slots, in this order, validated all-pairs in both light and dark: every pair is on
 * screen at once in a scatter, so the adjacent-pair check that suits a bar chart is not
 * the relevant gate. Worst pair is green against yellow at CVD delta-E 6.8 (protan),
 * inside the 6-8 band, which is permitted only alongside a second channel - here the
 * legend and the hover label. Adding a fifth slot fails outright: brand purple collides
 * with blue at delta-E 3.0 for deuteranopes, and orange with yellow at 7.5 even in normal
 * vision. Past four types, fold the tail into one "other" colour rather than inventing a
 * fifth hue.
 */
export const CATEGORICAL_FALLBACK = [
  '#2563EB', // blue600
  '#E17100', // yellow600
  '#0F8655', // green700
  '#D623B3', // pink600
] as const;

const SRGB_TO_LINEAR_EXPONENT = 2.4;

const toLinear = (channel: number): number => {
  const normalised = channel / 255;
  return normalised <= 0.04045
    ? normalised / 12.92
    : Math.pow((normalised + 0.055) / 1.055, SRGB_TO_LINEAR_EXPONENT);
};

const toSrgb = (linear: number): number => {
  const clamped = Math.max(0, Math.min(1, linear));
  const encoded =
    clamped <= 0.0031308
      ? clamped * 12.92
      : 1.055 * Math.pow(clamped, 1 / SRGB_TO_LINEAR_EXPONENT) - 0.055;
  return Math.round(encoded * 255);
};

/**
 * Mixes two ramp steps in linear-light space rather than straight on the encoded bytes.
 *
 * Averaging sRGB values directly darkens every midpoint, because sRGB is gamma-encoded -
 * the halfway byte is nowhere near half the light. On a continuous field that shows up as
 * a dark seam between each pair of steps, which reads as a contour line that is not in
 * the data.
 */
const mix = (from: Rgb, to: Rgb, t: number): Rgb => [
  toSrgb(toLinear(from[0]) + (toLinear(to[0]) - toLinear(from[0])) * t),
  toSrgb(toLinear(from[1]) + (toLinear(to[1]) - toLinear(from[1])) * t),
  toSrgb(toLinear(from[2]) + (toLinear(to[2]) - toLinear(from[2])) * t),
];

export type ColorScale = {
  /** Samples the ramp at a normalised position, clamped to [0, 1]. */
  at: (t: number) => Rgb;
  /** Maps a value in the field's own units through the ramp. */
  forValue: (value: number) => Rgb;
  /** Band edges in field units when quantised, ascending; empty when continuous. */
  bandEdges: number[];
  /** The field range the scale maps, so a caller can normalise without re-deriving it. */
  range: NumericRange;
  /** How many bands the ramp is quantised into; 0 when continuous. */
  bands: number;
};

export type ColorScaleArgs = {
  range: NumericRange;
  ramp: SequentialRampName;
  /**
   * Quantises the ramp into this many bands. 0 leaves it continuous.
   *
   * Banding is what turns a smooth field into a readable one: it lets a viewer say "this
   * region is one step higher than that" instead of squinting at a gradient, and the band
   * boundaries coincide with the isolines drawn on top.
   */
  bands: number;
};

export const createColorScale = ({
  range: [min, max],
  ramp,
  bands,
}: ColorScaleArgs): ColorScale => {
  const steps = SEQUENTIAL_RAMPS[ramp];
  const span = max - min;

  const at = (t: number): Rgb => {
    const clamped = Math.max(0, Math.min(1, t));
    /* One band is one colour, and `bands - 1` divides it to the ramp's first step - which is
       near-white, 16/255 from the default surface, so the whole plot came out blank rather than
       obviously single-banded. The midpoint is the one colour a single band can wear and still be
       read as a value. */
    const quantised =
      bands > 1
        ? Math.min(bands - 1, Math.floor(clamped * bands)) / (bands - 1)
        : bands === 1
          ? 0.5
          : clamped;
    const scaled = quantised * (steps.length - 1);
    const lower = Math.floor(scaled);
    const upper = Math.min(steps.length - 1, lower + 1);

    return mix(steps[lower], steps[upper], scaled - lower);
  };

  return {
    at,
    forValue: value => at(span === 0 ? 0.5 : (value - min) / span),
    bandEdges:
      bands > 0
        ? Array.from(
            { length: bands - 1 },
            (_, index) => min + ((index + 1) / bands) * span
          )
        : [],
    range: [min, max],
    bands,
  };
};

export const toCssColor = ([r, g, b]: Rgb): string => `rgb(${r}, ${g}, ${b})`;
